import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Input } from 'antd';
import axios from 'axios';
import { API_DOMAIN } from '../constants';
import useSnackbar from '../Snackbar/UseSnackbar';
import siteConfig from '../../siteConfig';
import { countSrmChangelogItems } from '../../utils/srmVersion';

// Update this together with siteConfig.version/build_no before each rollout.
// It is only the text area's initial value, so the publisher can freely edit
// the release notes in Global Settings before broadcasting them.
const DEFAULT_CHANGELOG = `✨ What's new in SRM v5.2.0

📊 Sales Pipeline Insights
See each stage's share of the active pipeline and open a detailed salesperson breakdown with totals, percentages and stage-by-stage progress.

💬 Internal Chat Alerts Everywhere
Internal messages now produce global in-app and browser notifications across SRM, not only while working in the Sales Pipeline.

💳 Live Accounts Work Notifications
Accounts users are alerted when new Payments V2 work needs attention, with live pending counts and direct navigation to the affected queue item.

🚦 Live Operations Action Alerts
Operations users now receive global alerts when work needs attention, can open the affected order directly, and see the dispatcher refresh automatically when work is created or resolved.

🕘 Customer Chat Assignment History
Review WATI customer-chat assignment changes with useful filters, user details and a clearer history workspace in Global Settings.

🪟 Roomier Settings Workspaces
Global and user settings now use more of the available screen space, with improved filter layouts for assignment history.

🛡️ More Reliable Sales Drawers
Task and Manage Prices drawers retain their loaded information through API refreshes, malformed responses and overlapping live updates.`;

export function UpdateNotificationPanel() {
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const { SuccessSnackbar, ErrorSnackbar } = useSnackbar();

  const [changelog, setChangelog] = useState(DEFAULT_CHANGELOG);
  const [requireLogout, setRequireLogout] = useState(false);
  const [pushing, setPushing] = useState(false);
  const changeCount = countSrmChangelogItems(changelog);

  const handlePush = async () => {
    setPushing(true);
    try {
      const res = await axios.post(`${API_DOMAIN}/whatsapp/push_new_version_event`, {
        changelog,
        version: siteConfig.version,
        build_no: siteConfig.build_no,
        published_at: new Date().toISOString(),
        require_logout: requireLogout,
        change_count: changeCount,
      }, head);
      if (res.data.success) {
        SuccessSnackbar("Update notification pushed!");
        setChangelog(DEFAULT_CHANGELOG);
        setRequireLogout(false);
      } else {
        ErrorSnackbar(res.data.message);
      }
    } catch (error) {
      ErrorSnackbar(error.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div style={{ fontSize: 12, color: "#595959", marginBottom: 10 }}>
          Publishing <strong>SRM v{siteConfig.version}</strong> · build <strong>{siteConfig.build_no}</strong>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Changelog</div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
          Description of the latest software update — shown to users in the update notification.
        </div>
        <Input.TextArea
          rows={8}
          value={changelog}
          onChange={e => setChangelog(e.target.value)}
          placeholder="What's new in this update…"
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c" }}>
          {changeCount > 0 ? `${changeCount} update ${changeCount === 1 ? "item" : "items"} detected for the banner.` : "The banner will use a generic update message."}
        </div>
      </div>
      <Checkbox checked={requireLogout} onChange={event => setRequireLogout(event.target.checked)}>
        Require users to log in again after loading this update
      </Checkbox>
      {requireLogout && (
        <div style={{ marginTop: -6, fontSize: 12, color: "#8c8c8c" }}>
          Users will be warned that loading this version clears their current session and redirects them to the login screen.
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" loading={pushing} disabled={!changelog.trim()} onClick={handlePush}>
          Push update notif
        </Button>
      </div>
    </div>
  );
}
